import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { UserPlus, UserCheck, Pencil, Loader2 } from "lucide-react";
import api, { mediaUrl, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ProjectCard } from "@/components/ProjectCard";
import { Loader, EmptyState } from "@/components/Loader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";

const BANNER = "https://images.unsplash.com/photo-1597960194599-22929afc25b1?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

export default function UserProfile() {
  const { username } = useParams();
  const { user, setUser, refreshNotifications } = useAuth();
  const [profile, setProfile] = useState(null);
  const [notFound, setNotFound] = useState(false);

  const load = () => api.get(`/users/${username}`).then((r) => setProfile(r.data)).catch(() => setNotFound(true));
  useEffect(() => { setProfile(null); setNotFound(false); load(); /* eslint-disable-next-line */ }, [username]);

  const follow = async () => {
    if (!user) return;
    try {
      const { data } = await api.post(`/users/${username}/follow`);
      setProfile((p) => ({ ...p, is_following: data.is_following, followers: data.followers }));
      refreshNotifications();
    } catch { toast.error("Erro ao seguir"); }
  };

  if (notFound) return <EmptyState title="Usuário não encontrado." />;
  if (!profile) return <Loader full />;

  return (
    <div>
      <div className="relative h-40 sm:h-52 w-full overflow-hidden wood-grain">
        <img src={BANNER} alt="" className="h-full w-full object-cover opacity-90" />
        <div className="absolute inset-0 bg-gradient-to-t from-background to-transparent" />
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 -mt-16 relative z-10">
        <div className="flex flex-col sm:flex-row sm:items-end gap-4">
          <img src={mediaUrl(profile.avatar)} alt={profile.name} className="h-28 w-28 rounded-2xl border-4 border-background object-cover shadow-lg" />
          <div className="flex-1 pb-2 sm:pt-16">
            <h1 className="text-2xl font-extrabold tracking-tight">{profile.name}</h1>
            <p className="text-muted-foreground">@{profile.username}</p>
          </div>
          <div className="pb-2">
            {profile.is_me ? (
              <EditProfileDialog profile={profile} onSaved={(u) => { setProfile((p) => ({ ...p, ...u })); setUser((cur) => ({ ...cur, ...u })); }} />
            ) : user ? (
              <Button data-testid="follow-btn" onClick={follow} variant={profile.is_following ? "outline" : "default"} className="rounded-full gap-1.5">
                {profile.is_following ? <><UserCheck className="h-4 w-4" /> Seguindo</> : <><UserPlus className="h-4 w-4" /> Seguir</>}
              </Button>
            ) : null}
          </div>
        </div>

        {profile.bio && <p className="mt-4 max-w-2xl text-sm leading-relaxed text-muted-foreground">{profile.bio}</p>}

        <div className="mt-5 flex gap-6">
          <Stat value={profile.projects_count} label="Projetos" />
          <Stat value={profile.followers} label="Seguidores" />
          <Stat value={profile.following} label="Seguindo" />
        </div>

        <div className="mt-8">
          <h2 className="mb-4 text-xl font-bold">Projetos publicados</h2>
          {profile.projects?.length ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {profile.projects.map((p, i) => <ProjectCard key={p.id} project={p} index={i} />)}
            </div>
          ) : (
            <EmptyState title="Nenhum projeto ainda." subtitle={profile.is_me ? "Que tal publicar o primeiro?" : ""} />
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ value, label }) {
  return <div><span className="text-xl font-extrabold">{value}</span> <span className="text-sm text-muted-foreground">{label}</span></div>;
}

function EditProfileDialog({ profile, onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: profile.name, bio: profile.bio || "", avatar: profile.avatar || "" });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/users/me", form);
      onSaved(data);
      toast.success("Perfil atualizado!");
      setOpen(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="edit-profile-btn" variant="outline" className="rounded-full gap-1.5"><Pencil className="h-4 w-4" /> Editar perfil</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Editar perfil</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><Label>Nome</Label><Input data-testid="edit-name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="mt-1.5 rounded-xl" /></div>
          <div><Label>Biografia</Label><Textarea data-testid="edit-bio" value={form.bio} onChange={(e) => setForm((f) => ({ ...f, bio: e.target.value }))} className="mt-1.5 rounded-xl" /></div>
          <div><Label>URL da foto de perfil</Label><Input data-testid="edit-avatar" value={form.avatar} onChange={(e) => setForm((f) => ({ ...f, avatar: e.target.value }))} className="mt-1.5 rounded-xl" placeholder="https://..." /></div>
        </div>
        <DialogFooter>
          <Button data-testid="save-profile-btn" onClick={save} disabled={saving} className="rounded-full">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salvar"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
